import React, { useEffect, useRef, useState } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import desiriUserService from "../../services/desiriUser/userservice";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { DesiriUser } from "../../models/desiri-user";

interface Props {
  data: DesiriUser[];
  onRefresh: () => void;
}

const DesiriUserTable: React.FC<Props> = ({ data, onRefresh }) => {
  const toast = useRef<Toast>(null);
  const [MUOptions, setMUOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        style={{ width: "90%", border: "1px solid #090" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };

  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: `Are you sure you want to delete "${rowData.name}"?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };

  const handleDelete = async (rowData: any) => {
    try {
      const res = await desiriUserService.deleteUser(rowData.id);

      if (res.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Delete User",
          detail: res.message,
          life: 3000,
        });
        onRefresh();
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Delete User",
          detail: res.message || "Failed to delete user.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.current?.show({
        severity: "error",
        summary: "Delete User",
        detail: "An error occurred while deleting the user.",
        life: 3000,
      });
    }
  };

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const newData = event.newData as DesiriUser;

    // Validation
    const errors: string[] = [];
    if (!newData.name?.trim()) errors.push("Name is required");
    if (!newData.addressline1?.trim()) errors.push("addressline1 is required");
    if (!newData.primaryContactNbr?.trim())
      errors.push("Primary Contact is required");
    if (!newData.designationID) errors.push("Designation is required");
    if (!newData.gender) errors.push("Gender is required");
    if (!newData.dob) errors.push("DOB is required");
    if (!newData.posID) errors.push("POS is required");

    if (errors.length > 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: errors.join(", "),
        life: 4000,
      });
      return;
    }

    try {
      const res = await desiriUserService.updateUser(newData);
      if (res.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Update User",
          detail: res.message,
          life: 3000,
        });
        onRefresh?.();
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Update User",
          detail: res.message || "Failed to update user.",
          life: 3000,
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Update User",
        detail: "An error occurred while updating the user.",
        life: 3000,
      });
    }
  };

  const govtIdOptions = [
    { label: "PAN", value: "PAN" },
    { label: "Aadhaar", value: "Aadhaar" },
    { label: "Aadhar", value: "Aadhar" },
    { label: "Others", value: "Others" },
  ];

  const govtIdEditor = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={govtIdOptions}
        onChange={(e) => options.editorCallback(e.value)}
        placeholder="Select Govt ID"
        className="w-full"
      />
    );
  };
  const dobEditor = (options: any) => {
    return (
      <Calendar
        value={options.value ? new Date(options.value) : null}
        onChange={(e) => {
          if (e.value instanceof Date) {
            const date = e.value;
            const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            options.editorCallback(isoDate);
          } else {
            options.editorCallback(null);
          }
        }}
        dateFormat="yy-mm-dd"
        className="w-full"
      />
    );
  };

  const [designations, setDesignations] = useState<any[]>([]);

  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const response = await desiriUserService.getAllDesignations();
        const formatted = response.map((d: any) => ({
          label: d.name,
          value: d.id,
        }));
        setDesignations(formatted);
      } catch (error) {
        console.error("Failed to load designations:", error);
      }
    };

    fetchDesignations();
  }, []);

  const designationEditor = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={designations}
        onChange={(e) => options.editorCallback(e.value)}
        placeholder="Select Designation"
        filter
        showClear
        className="w-full"
      />
    );
  };
  useEffect(() => {
    const fetchMUPlants = async () => {
      try {
        const response = await desiriUserService.getAllMUPlants1();
        const formatted = response.map((d: any) => ({
          label: d.posName,
          value: d.posID,
        }));
        setMUOptions(formatted);
      } catch (error) {
        console.error("Failed to load Manufacture Plants:", error);
      }
    };

    fetchMUPlants();
  }, []);

  const muEditor = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={MUOptions}
        onChange={(e) => options.editorCallback(e.value)}
        aria-label="POS"
        placeholder="Select POS"
        filter
        showClear
        className="w-full"
      />
    );
  };
  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
  ];

  const genderEditor = (options: any) => (
    <Dropdown
      value={options.value}
      options={genderOptions}
      onChange={(e) => options.editorCallback(e.value)}
      placeholder="Select Gender"
      className="w-full"
    />
  );

  const sortedData = [...data].sort((a, b) => b.id - a.id);

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card p-2">
        <DataTable
          value={sortedData}
          editMode="row"
          dataKey="id"
          onRowEditComplete={onRowEditComplete}
          paginator
          rowsPerPageOptions={[5, 10, 25, 50]}
          rows={5}
          stripedRows
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
              scrollHeight=""  
           scrollable 
        >
          <Column
            field="name"
            header="Name"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="addressline1"
            header="Address Line 1"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="addressline2"
            header="Address Line 2"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="govtIDName"
            header="Govt ID Name"
            editor={govtIdEditor}
            sortable
            style={{ width: "25%" }}
          />

          <Column
            field="govtIDNumber"
            header="Govt ID Number"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="dob"
            header="DOB"
            body={(rowData) => {
              if (!rowData.dob) return "";
              const date = new Date(rowData.dob);
              const day = String(date.getDate()).padStart(2, "0");
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = String(date.getFullYear()).slice(-2);
              return `${day}/${month}/${year}`;
            }}
            editor={dobEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="primaryContactNbr"
            header="Primary Contact"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="secondaryContactNbr"
            header="Secondary Contact"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="designationID"
            header="Designation"
            body={(rowData) => {
              const designation = designations.find(
                (d) => d.value === rowData.designationID
              );
              return designation ? designation.label : "";
            }}
            editor={designationEditor}
            sortable
            
            style={{ width: "25%" }}
          />
          <Column
            field="gender"
            header="Gender"
            editor={genderEditor}
            sortable
            style={{ width: "25%" }}
          />

          <Column
            field="posID"
            header="POS"
            body={(rowData) => {
              const mu = MUOptions.find((m) => m.value === rowData.posID);
              return mu ? mu.label : "";
            }}
            editor={muEditor}
            sortable
            
            style={{ width: "25%" }}
          />

          <Column
            field="esinumber"
            header="ESI Number"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="pfnumber"
            header="PF Number"
            sortable
            editor={textEditor}
            style={{ width: "25%" }}
          />
          <Column
            field="bankNumber"
            header="Bank Number"
            editor={textEditor}
            sortable
            style={{ width: "25%" }}
          />
          <Column
            rowEditor
            header="Action"
            headerStyle={{ textAlign: "right" }}
            frozen
            alignFrozen="right"
          />
          <Column
            header={"Delete"}
            body={(rowData) => (
              <Button
              
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-danger p-button-text"
              />
            )}
            style={{ width: "15%" }}
             frozen
            alignFrozen="right"
          />
        </DataTable>
      </div>
    </div>
  );
};

export default DesiriUserTable;
